'use client';

import type { HeadingBlock } from '@impact/types';
import type { ElementType } from 'react';

interface Props {
  block: HeadingBlock;
  editing?: boolean;
}

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4';

const headingSizes: Record<HeadingLevel, string> = {
  h1: 'text-4xl md:text-5xl font-bold',
  h2: 'text-3xl md:text-4xl font-bold',
  h3: 'text-2xl md:text-3xl font-semibold',
  h4: 'text-xl md:text-2xl font-semibold',
};

/**
 * Eyebrow stacked variant: subtitle above title with decorative line
 */
export function HeadingEyebrowStacked({ block, editing }: Props) {
  const { text, level, subtitle, alignment = 'left' } = block.data;
  const Tag = level as ElementType;

  const alignClasses = {
    left: 'text-left',
    center: 'text-center items-center',
    right: 'text-right items-end',
  };

  return (
    <div className={`my-8 flex flex-col gap-3 ${alignClasses[alignment]}`}>
      {subtitle ? (
        <div
          className={`flex items-center gap-3 ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : ''}`}
        >
          <span className="h-px w-8 bg-primary flex-shrink-0" />
          <span
            className="text-xs font-semibold uppercase tracking-widest text-primary"
            {...(editing ? { 'data-editable': 'data.subtitle' } : {})}
          >
            {subtitle}
          </span>
          <span className="h-px w-8 bg-primary flex-shrink-0" />
        </div>
      ) : null}
      <Tag
        className={`${headingSizes[level as HeadingLevel]} text-foreground`}
        {...(editing ? { 'data-editable': 'data.text' } : {})}
      >
        {text}
      </Tag>
    </div>
  );
}
